import { useState, useRef } from 'react';
import { Upload, X, Check, AlertCircle, Image, FileText, Plus } from 'lucide-react';
import vercelBlobService from '../services/vercelBlobService';

const VercelBlobUpload = ({ 
  onUploadSuccess, 
  onUploadError, 
  allowMultiple = true,
  acceptedTypes = "image/*",
  maxFileSize = 10 * 1024 * 1024, // 10MB
  showMetadataForm = true,
  defaultCategory = "",
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [metadata, setMetadata] = useState({
    title: '',
    description: '',
    category: defaultCategory,
    price: '',
    currency: 'K',
    in_stock: true,
    featured: false
  });
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  
  const fileInputRef = useRef(null);

  const categories = [
    'Arduino Boards',
    'Sensors', 
    'Displays',
    'Communication Modules',
    'Motors & Actuators',
    'Relays & Switches',
    'Power Supplies',
    'Breadboards & Prototyping',
    'Components'
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      if (file.size > maxFileSize) {
        alert(`File ${file.name} is too large. Maximum size is ${maxFileSize / 1024 / 1024}MB`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles(validFiles);
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setUploadResults([]);
    
    try {
      const results = [];
      
      for (const file of selectedFiles) {
        console.log(`🚀 Starting upload for ${file.name}...`);
        
        let result;
        
        if (file.type.startsWith('image/')) {
          // Upload as product image with metadata
          const categoryToUse = showCategoryInput && newCategory ? newCategory : metadata.category;
          result = await vercelBlobService.uploadProductImage(file, categoryToUse, {
            title: metadata.title || file.name.replace(/\.[^/.]+$/, ""),
            description: metadata.description,
            price: metadata.price ? parseFloat(metadata.price) : null,
            currency: metadata.currency,
            in_stock: metadata.in_stock,
            featured: metadata.featured
          });
        } else {
          // Upload as regular file
          result = await vercelBlobService.uploadFile(file, file.name);
        }
        
        results.push(result);
        
        if (result.success) {
          console.log(`✅ Successfully uploaded ${file.name}`);
          if (onUploadSuccess) {
            onUploadSuccess(result);
          }
        } else {
          console.error(`❌ Failed to upload ${file.name}:`, result.error);
          if (onUploadError) {
            onUploadError(result);
          }
        }
      }
      
      setUploadResults(results);
      
      // Clear form after successful upload
      const successfulUploads = results.filter(r => r.success);
      if (successfulUploads.length > 0) {
        setSelectedFiles([]);
        setMetadata({
          title: '',
          description: '',
          category: defaultCategory,
          price: '',
          currency: 'K',
          in_stock: true,
          featured: false
        });
        setNewCategory('');
        setShowCategoryInput(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      if (onUploadError) {
        onUploadError({ success: false, error: error.message });
      }
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const addNewCategory = () => {
    if (newCategory.trim()) {
      setMetadata(prev => ({ ...prev, category: newCategory.trim() }));
      setShowCategoryInput(false);
      setNewCategory('');
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <div className="flex items-center mb-6">
        <Upload className="w-6 h-6 text-blue-600 mr-3" />
        <div>
          <h3 className="text-xl font-bold text-gray-900">Vercel Blob Upload</h3>
          <p className="text-gray-600">Upload files directly to Vercel Blob storage</p>
        </div>
      </div>

      {/* File Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={allowMultiple}
          accept={acceptedTypes}
          onChange={handleChange}
          className="hidden"
        />
        
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-gray-700 mb-2">
          Drop files here or click to browse
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Maximum file size: {maxFileSize / 1024 / 1024}MB
        </p>
        
        <button
          onClick={() => fileInputRef.current?.click()}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Select Files
        </button>
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-gray-900 mb-3">Selected Files ({selectedFiles.length})</h4>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center">
                  {file.type.startsWith('image/') ? (
                    <Image className="w-5 h-5 text-green-600 mr-2" />
                  ) : (
                    <FileText className="w-5 h-5 text-blue-600 mr-2" />
                  )}
                  <span className="text-sm font-medium">{file.name}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata Form */}
      {showMetadataForm && selectedFiles.some(f => f.type.startsWith('image/')) && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Product Metadata</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={metadata.title}
                onChange={(e) => setMetadata(prev => ({ ...prev, title: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Product title"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <div className="flex gap-2">
                {!showCategoryInput ? (
                  <>
                    <select
                      value={metadata.category}
                      onChange={(e) => setMetadata(prev => ({ ...prev, category: e.target.value }))}
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowCategoryInput(true)}
                      className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      title="Add new category"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="New category name"
                    />
                    <button
                      onClick={addNewCategory}
                      className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setShowCategoryInput(false)}
                      className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={metadata.description}
                onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Product description"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={metadata.price}
                  onChange={(e) => setMetadata(prev => ({ ...prev, price: e.target.value }))}
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  step="0.01"
                />
                <select
                  value={metadata.currency}
                  onChange={(e) => setMetadata(prev => ({ ...prev, currency: e.target.value }))}
                  className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="K">K</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={metadata.in_stock}
                  onChange={(e) => setMetadata(prev => ({ ...prev, in_stock: e.target.checked }))}
                  className="mr-2"
                />
                In Stock
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={metadata.featured}
                  onChange={(e) => setMetadata(prev => ({ ...prev, featured: e.target.checked }))}
                  className="mr-2"
                />
                Featured
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFiles.length > 0 && (
        <div className="mt-6">
          <button
            onClick={uploadFiles}
            disabled={uploading}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {uploading ? (
              <>
                <AlertCircle className="w-5 h-5 mr-2 animate-spin" />
                Uploading {selectedFiles.length} file(s)...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Upload {selectedFiles.length} file(s) to Vercel Blob
              </>
            )}
          </button>
        </div>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-gray-900 mb-3">Upload Results</h4>
          <div className="space-y-2">
            {uploadResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg flex items-center justify-between ${
                  result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}
              >
                <div className="flex items-center">
                  {result.success ? (
                    <Check className="w-5 h-5 text-green-600 mr-2" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  )}
                  <span className="text-sm font-medium">{result.filename}</span>
                </div>
                {result.success ? (
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    View File
                  </a>
                ) : (
                  <span className="text-red-600 text-sm">{result.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VercelBlobUpload;
